/* ── BYO Shared Utilities (available immediately for byo.js) ────────── */

const BYO_STORAGE_KEY = 'ch_byo_lists';

const loadByoState = () => {
  try {
    const raw = localStorage.getItem(BYO_STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) { /* ignore parse errors */ }
  return { activeListId: null, lists: {} };
};

const saveByoState = (state) => {
  localStorage.setItem(BYO_STORAGE_KEY, JSON.stringify(state));
};

const getDiscountPercent = (itemCount) => {
  const configEl = document.querySelector('[data-byo-config]');
  if (configEl) {
    try {
      const config = JSON.parse(configEl.textContent);
      const tiers = config.tiers.slice().sort((a, b) => b.count - a.count);
      for (const tier of tiers) {
        if (itemCount >= tier.count) return tier.percent;
      }
      return 0;
    } catch (e) { /* fall through to defaults */ }
  }
  if (itemCount >= 7) return 20;
  if (itemCount >= 5) return 15;
  if (itemCount >= 3) return 10;
  return 0;
};

const formatMoney = (cents) => '$' + (cents / 100).toFixed(2);

window.ChapterHouse = window.ChapterHouse || {};
window.ChapterHouse.BYO = {
  loadState: loadByoState,
  saveState: saveByoState,
  getDiscountPercent,
  formatMoney
};

/* ── DOM-Ready Init ────────────────────────────────────────────────── */

document.addEventListener('DOMContentLoaded', () => {
  const getTrackGap = (track) => {
    const styles = window.getComputedStyle(track);
    const gapValue = styles.columnGap || styles.gap || '0';
    const gap = parseFloat(gapValue);
    return Number.isNaN(gap) ? 0 : gap;
  };

  const measureTrackWidth = (items, gap) => {
    if (!items.length) return 0;
    const totalWidth = items.reduce((sum, item) => sum + item.offsetWidth, 0);
    const totalGap = gap * Math.max(items.length - 1, 0);
    return Math.max(0, totalWidth + totalGap);
  };

  const waitForImages = (container, callback) => {
    const images = Array.from(container.querySelectorAll('img'));
    const pending = images.filter((img) => !img.complete);
    if (!pending.length) {
      callback();
      return;
    }
    let remaining = pending.length;
    const done = () => {
      remaining -= 1;
      if (remaining === 0) {
        callback();
      }
    };
    pending.forEach((img) => {
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    });
  };

  const initRotatingBanners = (root = document) => {
    const banners = root.querySelectorAll('.site-header__rotatingBanner');
    banners.forEach((banner) => {
      const track = banner.querySelector('.site-header__rotatingBanner-track');
      if (!track) return;

      waitForImages(track, () => {});

      const existingClones = track.querySelectorAll('[data-banner-clone="true"]');
      existingClones.forEach((clone) => clone.remove());

      const originalItems = Array.from(track.children);
      if (!originalItems.length) return;

      const gap = getTrackGap(track);
      const width = measureTrackWidth(originalItems, gap);
      if (!width) return;

      const bannerWidth = banner.getBoundingClientRect().width;
      const minTotalWidth = width + bannerWidth;
      const copiesNeeded = Math.max(1, Math.ceil(minTotalWidth / width));

      for (let i = 1; i < copiesNeeded; i += 1) {
        originalItems.forEach((item) => {
          const clone = item.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          clone.dataset.bannerClone = 'true';
          track.appendChild(clone);
        });
      }

      const speed = parseFloat(banner.dataset.bannerSpeed || '40');
      const duration = Math.max(width / speed, 15);
      track.style.setProperty('--banner-width', `${width}px`);
      track.style.setProperty('--banner-duration', `${duration}s`);
    });
  };

  const initRotatingTracks = (root = document) => {
    const wrappers = root.querySelectorAll('[data-rotating]');
    wrappers.forEach((wrapper) => {
      const track = wrapper.querySelector('[data-rotating-track]');
      if (!track) return;

      waitForImages(track, () => initRotatingTracks(wrapper));

      const existingClones = track.querySelectorAll('[data-rotating-clone="true"]');
      existingClones.forEach((clone) => clone.remove());

      const originalItems = Array.from(track.children);
      if (!originalItems.length) return;

      const gap = getTrackGap(track);
      const width = measureTrackWidth(originalItems, gap);
      if (!width) return;

      const wrapperWidth = wrapper.getBoundingClientRect().width;
      const minTotalWidth = width + wrapperWidth;
      const copiesNeeded = Math.max(1, Math.ceil(minTotalWidth / width));

      for (let i = 1; i < copiesNeeded; i += 1) {
        originalItems.forEach((item) => {
          const clone = item.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          clone.dataset.rotatingClone = 'true';
          track.appendChild(clone);
        });
      }

      const speed = parseFloat(wrapper.dataset.rotatingSpeed || '30');
      const duration = Math.max(width / speed, 15);
      track.style.setProperty('--rotate-width', `${width}px`);
      track.style.setProperty('--rotate-duration', `${duration}s`);
    });
  };

  const variantSelector = document.getElementById('variant-selector');
  if (variantSelector) {
    variantSelector.addEventListener('change', (e) => {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', e.target.value);
      window.history.replaceState({}, '', url);
    });
  }

  /* ── Cart Drawer ─────────────────────────────────────────────────── */

  const openCartDrawer = () => {
    document.body.classList.add('cart-drawer-open');
    const drawer = document.querySelector('.cart-drawer__panel');
    if (drawer) {
      drawer.setAttribute('aria-hidden', 'false');
    }
  };

  const closeCartDrawer = () => {
    document.body.classList.remove('cart-drawer-open');
    const drawer = document.querySelector('.cart-drawer__panel');
    if (drawer) {
      drawer.setAttribute('aria-hidden', 'true');
    }
  };

  const updateCartCount = (count) => {
    document.querySelectorAll('.cart-count').forEach((node) => {
      node.textContent = count;
    });
  };

  const refreshCartDrawer = async () => {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const wasOpen = document.body.classList.contains('cart-drawer-open');
    const response = await fetch(`${root}?sections=cart-drawer`);
    const data = await response.json();
    const html = data['cart-drawer'];
    if (!html) return;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newDrawer = doc.querySelector('.cart-drawer');
    const currentDrawer = document.querySelector('.cart-drawer');
    if (newDrawer && currentDrawer) {
      currentDrawer.replaceWith(newDrawer);
    }

    if (wasOpen) {
      openCartDrawer();
    }

    renderByoCartGroups();
    document.dispatchEvent(new CustomEvent('cart-drawer:refreshed'));
  };

  const changeLineItem = async (key, quantity) => {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
    if (!response.ok) return;
    const cart = await response.json();
    updateCartCount(cart.item_count);
    await refreshCartDrawer();
  };

  const changeBundleItems = async (keys, quantity) => {
    const updates = {};
    keys.split(',').forEach((key) => { updates[key] = quantity; });
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });
    if (!response.ok) return;
    const cart = await response.json();
    updateCartCount(cart.item_count);
    await refreshCartDrawer();
  };

  /* ── BYO Cart Drawer Rendering ───────────────────────────────────── */

  const trashSvg = '<svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="1.75" y="2.25" width="10.5" height="12.25" stroke="#111111"/><rect x="5.25" y="0.5" width="3.5" height="1.75" stroke="#111111"/><path d="M0 2.25H14" stroke="#111111"/><path d="M4.375 4.875V11.875" stroke="#111111"/><path d="M7 4.875V11.875" stroke="#111111"/><path d="M9.625 4.875V11.875" stroke="#111111"/></svg>';

  const renderByoCartGroups = () => {
    const container = document.querySelector('[data-byo-cart-lists]');
    if (!container) return;

    const state = loadByoState();
    container.innerHTML = '';

    let byoItemCount = 0;
    Object.values(state.lists).forEach((list) => {
      Object.values(list.items).forEach((item) => { byoItemCount += item.quantity; });
    });

    const emptyMsg = document.querySelector('[data-cart-empty]');
    if (emptyMsg) emptyMsg.style.display = byoItemCount > 0 ? 'none' : '';

    const shopifyCount = parseInt(document.querySelector('[data-byo-cart-total]')?.dataset.shopifyCount || '0', 10);
    updateCartCount(shopifyCount + byoItemCount);

    Object.values(state.lists).forEach((list) => {
      const items = Object.values(list.items);
      if (items.length === 0) return;

      const group = document.createElement('div');
      group.className = 'cart-drawer__group';
      group.dataset.byoCartList = list.id;

      const title = document.createElement('h3');
      title.className = 'cart-drawer__group-title';
      title.textContent = list.name;
      group.appendChild(title);

      items.forEach((item) => {
        const el = document.createElement('div');
        el.className = 'cart-drawer__byo-item';
        el.innerHTML =
          '<div class="cart-drawer__byo-image">' +
            '<img src="' + item.image + '" alt="' + item.title.replace(/"/g, '&quot;') + '" width="40" height="60" loading="lazy">' +
          '</div>' +
          '<div class="cart-drawer__byo-details">' +
            '<div class="cart-drawer__byo-row">' +
              '<span class="cart-drawer__byo-title">' + item.title + '</span>' +
              '<span class="cart-drawer__byo-price">' + formatMoney(item.price * item.quantity) + '</span>' +
            '</div>' +
            '<div class="cart-drawer__byo-controls">' +
              '<button type="button" class="cart-drawer__qty-btn" data-byo-cart-decrease="' + item.variantId + '" data-list-id="' + list.id + '">-</button>' +
              '<input class="cart-drawer__qty-input" type="number" min="1" value="' + item.quantity + '" data-byo-cart-qty="' + item.variantId + '" data-list-id="' + list.id + '">' +
              '<button type="button" class="cart-drawer__qty-btn" data-byo-cart-increase="' + item.variantId + '" data-list-id="' + list.id + '">+</button>' +
              '<button type="button" class="cart-drawer__byo-trash" data-byo-cart-remove="' + item.variantId + '" data-list-id="' + list.id + '">' + trashSvg + '</button>' +
            '</div>' +
          '</div>';
        group.appendChild(el);
      });

      container.appendChild(group);
    });

    updateByoCartFooter(state);
  };

  const updateByoCartFooter = (state) => {
    const discountRow = document.querySelector('[data-cart-discount-row]');
    const discountOriginalEl = document.querySelector('[data-cart-discount-original]');
    const totalEl = document.querySelector('[data-byo-cart-total]');
    if (!totalEl) return;

    const bundleTotal = parseInt(totalEl.dataset.shopifyBundleTotal || '0', 10);
    const regularTotal = parseInt(totalEl.dataset.shopifyRegularTotal || '0', 10);
    const regularCount = parseInt(totalEl.dataset.shopifyRegularCount || '0', 10);
    const shopifyCompareSavings = parseInt(totalEl.dataset.shopifyCompareSavings || '0', 10);

    let byoOriginal = 0;
    let byoItemCount = 0;

    Object.values(state.lists).forEach((list) => {
      const items = Object.values(list.items);
      byoOriginal += items.reduce((sum, item) => sum + item.price * item.quantity, 0);
      byoItemCount += items.length;
    });

    const eligibleCount = regularCount + byoItemCount;
    const discountPct = getDiscountPercent(eligibleCount);

    let eligibleDiscounted = 0;

    Object.values(state.lists).forEach((list) => {
      Object.values(list.items).forEach((item) => {
        const unitDiscount = Math.floor(item.price * (discountPct / 100));
        eligibleDiscounted += (item.price - unitDiscount) * item.quantity;
      });
    });

    const regularUnitDiscount = regularCount > 0 ? Math.floor((regularTotal / regularCount) * (discountPct / 100)) : 0;
    eligibleDiscounted += regularTotal - (regularUnitDiscount * regularCount);

    const grandOriginal = bundleTotal + regularTotal + shopifyCompareSavings + byoOriginal;
    const grandActual = bundleTotal + eligibleDiscounted;
    const hasSavings = grandOriginal > grandActual;

    if (discountRow && discountOriginalEl) {
      discountRow.style.display = hasSavings ? '' : 'none';
      if (hasSavings) {
        discountOriginalEl.textContent = formatMoney(grandOriginal);
      }
    }

    totalEl.textContent = formatMoney(grandActual);
  };

  /* ── BYO Cart Checkout Sync ──────────────────────────────────────── */

  const syncByoListsToCart = async () => {
    const state = loadByoState();
    const allItems = [];

    Object.values(state.lists).forEach((list) => {
      Object.values(list.items).forEach((item) => {
        allItems.push({
          id: parseInt(item.variantId, 10),
          quantity: item.quantity,
          properties: {
            '_bundle_id': list.id,
            '_bundle_title': list.name
          }
        });
      });
    });

    if (allItems.length === 0) {
      window.location.href = '/checkout';
      return;
    }

    const cartResp = await fetch('/cart.js');
    const cart = await cartResp.json();
    const byoKeys = cart.items
      .filter((item) => item.properties && item.properties['_bundle_id'] && state.lists[item.properties['_bundle_id']])
      .reduce((acc, item) => { acc[item.key] = 0; return acc; }, {});

    if (Object.keys(byoKeys).length > 0) {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: byoKeys })
      });
    }

    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: allItems })
    });

    window.location.href = '/checkout';
  };

  /* ── Cart Event Delegation ───────────────────────────────────────── */

  document.addEventListener('click', (event) => {
    const openTrigger = event.target.closest('[data-cart-drawer-open]');
    if (openTrigger) {
      event.preventDefault();
      openCartDrawer();
      refreshCartDrawer();
      return;
    }

    const increase = event.target.closest('[data-cart-qty-increase]');
    if (increase) {
      event.preventDefault();
      const key = increase.getAttribute('data-key');
      const input = document.querySelector(`[data-cart-qty-input][data-key="${key}"]`);
      const current = input ? parseInt(input.value, 10) : 1;
      const next = Math.max(1, current + 1);
      changeLineItem(key, next);
      return;
    }

    const decrease = event.target.closest('[data-cart-qty-decrease]');
    if (decrease) {
      event.preventDefault();
      const key = decrease.getAttribute('data-key');
      const input = document.querySelector(`[data-cart-qty-input][data-key="${key}"]`);
      const current = input ? parseInt(input.value, 10) : 1;
      const next = Math.max(1, current - 1);
      changeLineItem(key, next);
      return;
    }

    const remove = event.target.closest('[data-cart-remove]');
    if (remove) {
      event.preventDefault();
      const key = remove.getAttribute('data-key');
      changeLineItem(key, 0);
      return;
    }

    /* Bundle (Shopify) cart controls */
    const bundleIncrease = event.target.closest('[data-bundle-qty-increase]');
    if (bundleIncrease) {
      event.preventDefault();
      const keys = bundleIncrease.dataset.bundleKeys;
      const input = bundleIncrease.closest('[data-cart-bundle]').querySelector('[data-bundle-qty-input]');
      const next = Math.max(1, (parseInt(input.value, 10) || 1) + 1);
      changeBundleItems(keys, next);
      return;
    }

    const bundleDecrease = event.target.closest('[data-bundle-qty-decrease]');
    if (bundleDecrease) {
      event.preventDefault();
      const keys = bundleDecrease.dataset.bundleKeys;
      const input = bundleDecrease.closest('[data-cart-bundle]').querySelector('[data-bundle-qty-input]');
      const next = Math.max(1, (parseInt(input.value, 10) || 1) - 1);
      changeBundleItems(keys, next);
      return;
    }

    const bundleRemove = event.target.closest('[data-bundle-remove]');
    if (bundleRemove) {
      event.preventDefault();
      changeBundleItems(bundleRemove.dataset.bundleKeys, 0);
      return;
    }

    /* BYO cart item controls */
    const byoIncrease = event.target.closest('[data-byo-cart-increase]');
    if (byoIncrease) {
      event.preventDefault();
      const variantId = byoIncrease.dataset.byoCartIncrease;
      const listId = byoIncrease.dataset.listId;
      const state = loadByoState();
      const list = state.lists[listId];
      if (list && list.items[variantId]) {
        list.items[variantId].quantity += 1;
        saveByoState(state);
        renderByoCartGroups();
        document.dispatchEvent(new CustomEvent('byo:state-changed'));
      }
      return;
    }

    const byoDecrease = event.target.closest('[data-byo-cart-decrease]');
    if (byoDecrease) {
      event.preventDefault();
      const variantId = byoDecrease.dataset.byoCartDecrease;
      const listId = byoDecrease.dataset.listId;
      const state = loadByoState();
      const list = state.lists[listId];
      if (list && list.items[variantId]) {
        if (list.items[variantId].quantity > 1) {
          list.items[variantId].quantity -= 1;
        } else {
          delete list.items[variantId];
        }
        saveByoState(state);
        renderByoCartGroups();
        document.dispatchEvent(new CustomEvent('byo:state-changed'));
      }
      return;
    }

    const byoRemove = event.target.closest('[data-byo-cart-remove]');
    if (byoRemove) {
      event.preventDefault();
      const variantId = byoRemove.dataset.byoCartRemove;
      const listId = byoRemove.dataset.listId;
      const state = loadByoState();
      const list = state.lists[listId];
      if (list && list.items[variantId]) {
        delete list.items[variantId];
        saveByoState(state);
        renderByoCartGroups();
        document.dispatchEvent(new CustomEvent('byo:state-changed'));
      }
      return;
    }

    const checkout = event.target.closest('[data-byo-checkout]');
    if (checkout) {
      event.preventDefault();
      checkout.disabled = true;
      checkout.textContent = 'Processing...';
      const state = loadByoState();
      const hasByoItems = Object.values(state.lists).some((list) => Object.keys(list.items).length > 0);
      if (hasByoItems) {
        syncByoListsToCart();
      } else {
        window.location.href = '/checkout';
      }
      return;
    }

    if (document.body.classList.contains('cart-drawer-open')) {
      const panel = document.querySelector('.cart-drawer__panel');
      if (panel && !panel.contains(event.target)) {
        closeCartDrawer();
      }
    }
  });

  document.addEventListener('change', (event) => {
    const bundleInput = event.target.closest('[data-bundle-qty-input]');
    if (bundleInput) {
      const keys = bundleInput.dataset.bundleKeys;
      const quantity = Math.max(1, parseInt(bundleInput.value, 10) || 1);
      changeBundleItems(keys, quantity);
      return;
    }

    const input = event.target.closest('[data-cart-qty-input]');
    if (input) {
      const key = input.getAttribute('data-key');
      const quantity = Math.max(1, parseInt(input.value, 10) || 1);
      changeLineItem(key, quantity);
      return;
    }

    const byoInput = event.target.closest('[data-byo-cart-qty]');
    if (byoInput) {
      const variantId = byoInput.dataset.byoCartQty;
      const listId = byoInput.dataset.listId;
      const quantity = Math.max(1, parseInt(byoInput.value, 10) || 1);
      const state = loadByoState();
      const list = state.lists[listId];
      if (list && list.items[variantId]) {
        list.items[variantId].quantity = quantity;
        saveByoState(state);
        renderByoCartGroups();
      }
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeCartDrawer();
    }
  });

  /* ── AJAX Add-to-Cart (Product Pages) ────────────────────────────── */

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[action*="/cart/add"]');
    if (!form) return;
    event.preventDefault();

    const submitBtn = form.querySelector('[type="submit"]');
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Adding...';
    }

    try {
      const formData = new FormData(form);
      await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });
      const cartResp = await fetch('/cart.js');
      const cartData = await cartResp.json();
      updateCartCount(cartData.item_count);
      await refreshCartDrawer();
      openCartDrawer();
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = 'Add to cart';
      }
    }
  });

  /* ── Mobile Menu ─────────────────────────────────────────────────── */

  const mobileMenuToggle = document.querySelector('[data-mobile-menu-toggle]');
  if (mobileMenuToggle) {
    const setMobileMenuState = (isOpen) => {
      document.body.classList.toggle('mobile-menu-open', isOpen);
      mobileMenuToggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    };

    mobileMenuToggle.addEventListener('click', () => {
      const isOpen = document.body.classList.contains('mobile-menu-open');
      setMobileMenuState(!isOpen);
    });

    document.addEventListener('click', (event) => {
      if (!document.body.classList.contains('mobile-menu-open')) return;
      const menu = document.querySelector('.mobile-menu');
      if (!menu) return;
      if (menu.contains(event.target) || mobileMenuToggle.contains(event.target)) return;
      setMobileMenuState(false);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        setMobileMenuState(false);
      }
    });
  }

  /* ── Dropdowns ───────────────────────────────────────────────────── */

  const initDropdowns = (root = document) => {
    root.querySelectorAll('[data-dropdown]').forEach((dropdown) => {
      const trigger = dropdown.querySelector('[data-dropdown-trigger]');
      const menu = dropdown.querySelector('[data-dropdown-menu]');
      const valueEl = dropdown.querySelector('[data-dropdown-value]');
      if (!trigger || !menu) return;

      trigger.addEventListener('click', () => {
        const isOpen = dropdown.classList.contains('dropdown--open');
        document.querySelectorAll('.dropdown--open').forEach((d) => d.classList.remove('dropdown--open'));
        if (!isOpen) dropdown.classList.add('dropdown--open');
      });

      menu.querySelectorAll('[data-dropdown-option]').forEach((option) => {
        option.addEventListener('click', () => {
          menu.querySelectorAll('[data-dropdown-option]').forEach((o) => o.classList.remove('dropdown__option--active'));
          option.classList.add('dropdown__option--active');
          if (valueEl) valueEl.textContent = option.textContent;
          trigger.dataset.selectedValue = option.dataset.value;
          dropdown.classList.remove('dropdown--open');
          trigger.dispatchEvent(new Event('change'));
        });
      });
    });

    document.addEventListener('click', (event) => {
      if (!event.target.closest('[data-dropdown]')) {
        document.querySelectorAll('.dropdown--open').forEach((d) => d.classList.remove('dropdown--open'));
      }
    });
  };

  /* ── Curricula Filters ───────────────────────────────────────────── */

  const initCurriculaFilters = (root = document) => {
    const grid = root.querySelector('[data-curricula-grid]');
    const gradeFilter = root.querySelector('[data-curricula-grade-filter]');
    const sortSelect = root.querySelector('[data-curricula-sort]');
    const buyAllPanel = root.querySelector('[data-curricula-buy-all-panel]');
    const buyAllBtn = root.querySelector('[data-curricula-buy-all]');
    const buyAllLabel = root.querySelector('.curricula__buy-all-label');
    const buyAllPrice = root.querySelector('[data-curricula-buy-all-price]');
    if (!grid) return;

    const cards = Array.from(grid.querySelectorAll('.collection-card'));
    const originalOrder = cards.map((card) => card);

    const gradeOrder = ['kindergarten', '1st-grade', '2nd-grade', '3rd-grade', '4th-grade', '5th-grade', '6th-grade'];

    const minGradeIndex = (card) => {
      const grades = JSON.parse(card.dataset.gradeLevels || '[]');
      return grades.reduce((min, g) => {
        const idx = gradeOrder.indexOf(g);
        return idx !== -1 && idx < min ? idx : min;
      }, gradeOrder.length);
    };

    // Featured sort: curated order per the curricula spec:
    //   1. BUY EVERYTHING            (tier = 'everything')
    //   2. Chapter Complete Libraries (tier = 'complete-library', ordered by chapter num)
    //   3. Box Sets & Chapter Bundles (tier = 'combo' or 'box', merged into one section)
    //   4. Grade-level Bundles K→6   (tier = 'grade-bundle', ordered by grade)
    //   5. Anything unclassified      (tier = 'other')
    const TIER_RANK =  { 'box': 0, 'complete-library': 1, 'grade-bundle': 2, 'everything': 3, 'combo': 4, 'other': 4 };
    // Section heading per rank (combo + box share rank 2 → same section)
    const SECTION_HEADINGS = {
      0: { label: 'Chapter Box Sets', sub: 'Three classic books and a teaching guide in each set' },
      1: { label: 'Complete Libraries', sub: 'Box sets + matching grade-level bundles' },
      2: { label: 'Grade-Level Bundles', sub: 'Supplemental reading bundles, K through 6' },
      3: { label: 'Buy Everything', sub: 'The complete K–6 curriculum in one click' },
      4: { label: 'Other', sub: '' },
      5: { label: 'Other', sub: '' }
    };
    const applyFeaturedSort = (visible) => {
      return visible.slice().sort((a, b) => {
        const tA = TIER_RANK[a.dataset.curriculaTier] ?? 4;
        const tB = TIER_RANK[b.dataset.curriculaTier] ?? 4;
        if (tA !== tB) return tA - tB;
        // Within tier, sort by data-curricula-order (zero-padded string — lexicographic = numeric)
        const oA = a.dataset.curriculaOrder || '99';
        const oB = b.dataset.curriculaOrder || '99';
        if (oA !== oB) return oA < oB ? -1 : 1;
        // Final tiebreak: title
        return (a.dataset.title || '').localeCompare(b.dataset.title || '');
      });
    };

    // Inject section divider divs between tier groups in the rendered grid.
    // Removes any existing dividers first so re-sorts don't pile them up.
    const injectSectionDividers = (sortedCards) => {
      // Clear stale dividers
      grid.querySelectorAll('.curricula__grid-divider').forEach((d) => d.remove());

      let lastRank = -1;
      sortedCards.forEach((card) => {
        const rank = TIER_RANK[card.dataset.curriculaTier] ?? 4;
        if (rank !== lastRank) {
          const heading = SECTION_HEADINGS[rank];
          if (heading) {
            const divider = document.createElement('div');
            divider.className = 'curricula__grid-divider';
            divider.innerHTML =
              '<h3 class="curricula__grid-label">' + heading.label + '</h3>' +
              (heading.sub ? '<p class="curricula__grid-sub">' + heading.sub + '</p>' : '');
            grid.insertBefore(divider, card);
          }
          lastRank = rank;
        }
      });
    };

    const applyFilters = () => {
      const selectedGrade = gradeFilter ? gradeFilter.dataset.selectedValue || '' : '';
      const selectedSort = sortSelect ? sortSelect.dataset.selectedValue || 'featured' : 'featured';

      cards.forEach((card) => {
        if (!selectedGrade) {
          card.hidden = false;
          return;
        }
        // BUY EVERYTHING always visible regardless of grade filter
        if (card.dataset.curriculaTier === 'everything') {
          card.hidden = false;
          return;
        }
        const grades = JSON.parse(card.dataset.gradeLevels || '[]');
        card.hidden = !grades.includes(selectedGrade);
      });

      const visible = cards.filter((card) => !card.hidden);
      let sorted;

      switch (selectedSort) {
        case 'az':
          sorted = visible.slice().sort((a, b) =>
            a.dataset.title.localeCompare(b.dataset.title)
          );
          break;
        case 'za':
          sorted = visible.slice().sort((a, b) =>
            b.dataset.title.localeCompare(a.dataset.title)
          );
          break;
        case 'grade':
          sorted = visible.slice().sort((a, b) => minGradeIndex(a) - minGradeIndex(b));
          break;
        case 'featured':
        default:
          sorted = applyFeaturedSort(visible);
          break;
      }

      // Always remove any existing dividers before re-rendering
      grid.querySelectorAll('.curricula__grid-divider').forEach((d) => d.remove());

      sorted.forEach((card) => grid.appendChild(card));
      cards.filter((card) => card.hidden).forEach((card) => grid.appendChild(card));

      // Only inject section dividers in the curated "featured" view (and only when
      // there's no grade filter active — otherwise the user expects a flat filtered list)
      if (selectedSort === 'featured' && !selectedGrade) {
        injectSectionDividers(sorted);
      }

      updateBuyAllPanel(selectedGrade, visible);
    };

    // "Buy All" only adds the 4 solo Chapter box sets + 7 grade-level bundles.
    // Skips Chapter Complete Libraries (which already pair box+bundle together) and
    // the Chapter I-IV megabundle so users don't end up with duplicate copies.
    const BUY_ALL_TIERS = new Set(['box', 'grade-bundle']);
    const isBuyAllCandidate = (card) =>
      card.dataset.available !== 'false' && BUY_ALL_TIERS.has(card.dataset.curriculaTier || '');

    // "Buy All" panel: visible only when no grade filter is applied ("All Grades").
    // Price = sum of available products in the eligible tiers (boxes + grade bundles).
    const updateBuyAllPanel = (selectedGrade, visible) => {
      if (!buyAllPanel) return;
      const noFilter = !selectedGrade;
      const eligible = visible.filter(isBuyAllCandidate);
      const hasProducts = eligible.length > 0;
      buyAllPanel.hidden = !(noFilter && hasProducts);
      if (!noFilter || !hasProducts) return;

      const totalCents = eligible.reduce((sum, card) => {
        const p = parseInt(card.dataset.price, 10);
        return sum + (isNaN(p) ? 0 : p);
      }, 0);
      if (buyAllPrice) buyAllPrice.textContent = totalCents > 0 ? ' · ' + formatMoney(totalCents) : '';
    };

    if (buyAllBtn) {
      buyAllBtn.addEventListener('click', async (event) => {
        event.preventDefault();
        const visibleCards = cards.filter((card) => !card.hidden);
        // Only the 4 chapter box sets + 7 grade-level bundles. Anything sold-out
        // is filtered up front; if Shopify rejects a SKU (e.g. inventory race) we
        // skip it individually below.
        const candidates = visibleCards.filter(isBuyAllCandidate);
        if (candidates.length === 0) {
          alert('None of the eligible products are currently in stock.');
          return;
        }

        buyAllBtn.disabled = true;
        const originalLabel = buyAllLabel ? buyAllLabel.textContent : '';

        let added = 0;
        const failed = [];

        // Add one at a time — Shopify's /cart/add.js is atomic per request, so if we
        // batched them, one bad SKU would kill the whole add. Sequential lets us skip
        // failures individually.
        for (let i = 0; i < candidates.length; i += 1) {
          const card = candidates[i];
          if (buyAllLabel) buyAllLabel.textContent = `Adding… (${i + 1}/${candidates.length})`;
          const id = parseInt(card.dataset.variantId, 10);
          if (isNaN(id)) { failed.push(card.dataset.title || 'Unknown'); continue; }
          try {
            const res = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ items: [{ id, quantity: 1 }] })
            });
            if (!res.ok) {
              failed.push(card.dataset.title || 'Unknown');
              continue;
            }
            added += 1;
          } catch (err) {
            console.warn('[Curricula] individual add failed', card.dataset.title, err);
            failed.push(card.dataset.title || 'Unknown');
          }
        }

        if (buyAllLabel) buyAllLabel.textContent = originalLabel;
        buyAllBtn.disabled = false;

        if (added === 0) {
          alert('Could not add any products to cart. They may all be out of stock.');
          return;
        }

        // Open the cart drawer so the user can see what got added
        if (window.ChapterHouse && window.ChapterHouse.cart) {
          if (typeof window.ChapterHouse.cart.refresh === 'function') await window.ChapterHouse.cart.refresh();
          if (typeof window.ChapterHouse.cart.open === 'function') window.ChapterHouse.cart.open();
        }

        if (failed.length > 0) {
          const noun = failed.length === 1 ? 'product was' : 'products were';
          setTimeout(() => {
            alert(`Added ${added} to your cart. ${failed.length} ${noun} skipped (out of stock):\n\n• ${failed.join('\n• ')}`);
          }, 400);
        }
      });
    }

    if (gradeFilter) gradeFilter.addEventListener('change', applyFilters);
    if (sortSelect) sortSelect.addEventListener('change', applyFilters);

    applyFilters();
  };

  /* ── Global Namespace (cart functions need DOM, so exposed here) ─── */

  window.ChapterHouse.cart = {
    open: openCartDrawer,
    close: closeCartDrawer,
    refresh: refreshCartDrawer,
    changeLineItem,
    updateCount: updateCartCount
  };
  window.ChapterHouse.BYO.renderCartGroups = renderByoCartGroups;

  /* ── BYO Cart Cleanup (remove leftover synced items) ─────────────── */

  const cleanupByoCartItems = async () => {
    const resp = await fetch('/cart.js');
    const cart = await resp.json();
    const byoKeys = {};
    cart.items.forEach((item) => {
      if (item.properties && typeof item.properties['_bundle_id'] === 'string' && item.properties['_bundle_id'].startsWith('list_')) {
        byoKeys[item.key] = 0;
      }
    });
    if (Object.keys(byoKeys).length > 0) {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: byoKeys })
      });
      await refreshCartDrawer();
    }
  };

  /* ── Product Page ────────────────────────────────────────────────── */
  const initProductPage = () => {
  /* ── Product image gallery ───────────────────────────────────────── */
    const mainImage = document.querySelector('[data-product-main-image]');
    const mainPrevBtn = document.querySelector('[data-main-image-prev]');
    const mainNextBtn = document.querySelector('[data-main-image-next]');
    const thumbnails = Array.from(document.querySelectorAll('[data-product-thumbnail]'));
    const prevBtn = document.querySelector('[data-thumbnail-prev]');
    const nextBtn = document.querySelector('[data-thumbnail-next]');
    const thumbTrack = document.querySelector('[data-thumbnail-track]');

    let activeImageIndex = 0;

    const setActiveImage = (index) => {
      if (!mainImage || !thumbnails.length) return;

      const safeIndex = Math.max(0, Math.min(index, thumbnails.length - 1));
      const thumb = thumbnails[safeIndex];
      if (!thumb) return;

      activeImageIndex = safeIndex;

      mainImage.classList.add('is-changing');

      window.setTimeout(() => {
        mainImage.src = thumb.dataset.fullSrc;
        mainImage.alt = thumb.dataset.fullAlt || '';
        mainImage.width = thumb.dataset.fullWidth || mainImage.width;
        mainImage.height = thumb.dataset.fullHeight || mainImage.height;

        thumbnails.forEach((t) => t.classList.remove('product__thumbnail--active'));
        thumb.classList.add('product__thumbnail--active');

        thumb.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });

        mainImage.classList.remove('is-changing');
      }, 120);
    };

    if (mainPrevBtn) {
      mainPrevBtn.addEventListener('click', () => {
        const nextIndex = activeImageIndex === 0 ? thumbnails.length - 1 : activeImageIndex - 1;
        setActiveImage(nextIndex);
      });
    }

    if (mainNextBtn) {
      mainNextBtn.addEventListener('click', () => {
        const nextIndex = activeImageIndex === thumbnails.length - 1 ? 0 : activeImageIndex + 1;
        setActiveImage(nextIndex);
      });
    }
    if (mainImage && thumbnails.length) {
      thumbnails.forEach((thumb, index) => {
        thumb.classList.add('product__thumbnail--visible');

        thumb.addEventListener('click', () => {
          setActiveImage(index);
        });
      });

      if (prevBtn) {
        prevBtn.addEventListener('click', () => {
          const nextIndex =
            activeImageIndex === 0 ? thumbnails.length - 1 : activeImageIndex - 1;

          setActiveImage(nextIndex);
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', () => {
          const nextIndex =
            activeImageIndex === thumbnails.length - 1 ? 0 : activeImageIndex + 1;

          setActiveImage(nextIndex);
        });
      }

      document.addEventListener('keydown', (event) => {
        const productGallery = document.querySelector('.product__gallery');
        if (!productGallery) return;

        const galleryIsVisible = productGallery.getBoundingClientRect().top < window.innerHeight &&
          productGallery.getBoundingClientRect().bottom > 0;

        if (!galleryIsVisible) return;

        if (event.key === 'ArrowLeft') {
          const nextIndex =
            activeImageIndex === 0 ? thumbnails.length - 1 : activeImageIndex - 1;

          setActiveImage(nextIndex);
        }

        if (event.key === 'ArrowRight') {
          const nextIndex =
            activeImageIndex === thumbnails.length - 1 ? 0 : activeImageIndex + 1;

          setActiveImage(nextIndex);
        }
      });

      if (thumbTrack) {
        thumbTrack.addEventListener('wheel', (event) => {
          if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
            event.preventDefault();
            thumbTrack.scrollLeft += event.deltaY;
          }
        }, { passive: false });
      }
    }

    /* Quantity +/- buttons */
    const qtyInput = document.querySelector('[data-product-qty-input]');
    const qtyDecrease = document.querySelector('[data-product-qty-decrease]');
    const qtyIncrease = document.querySelector('[data-product-qty-increase]');

    if (qtyInput && qtyDecrease && qtyIncrease) {
      qtyDecrease.addEventListener('click', () => {
        const current = parseInt(qtyInput.value, 10) || 1;
        qtyInput.value = Math.max(1, current - 1);
      });
      qtyIncrease.addEventListener('click', () => {
        const current = parseInt(qtyInput.value, 10) || 1;
        qtyInput.value = current + 1;
      });
    }

    /* Add to Curriculum dropdown */
    const curriculumSection = document.querySelector('[data-product-curriculum]');
    if (curriculumSection) {
      const curriculumMenu = curriculumSection.querySelector('[data-curriculum-menu]');
      const curriculumModal = curriculumSection.querySelector('[data-curriculum-modal]');
      const curriculumInput = curriculumSection.querySelector('[data-curriculum-modal-input]');
      const curriculumValueEl = curriculumSection.querySelector('[data-dropdown-value]');
      const generateListId = () => 'list_' + Date.now() + '_' + Math.random().toString(36).substr(2, 6);

      const productData = {
        variantId: curriculumSection.dataset.productId,
        handle: curriculumSection.dataset.productHandle,
        title: curriculumSection.dataset.title,
        price: parseInt(curriculumSection.dataset.price, 10),
        priceFormatted: curriculumSection.dataset.priceFormatted,
        image: curriculumSection.dataset.image,
        quantity: 1
      };

      const renderCurriculumMenu = () => {
        if (!curriculumMenu) return;
        curriculumMenu.innerHTML = '';

        const createLi = document.createElement('li');
        createLi.innerHTML = '<button type="button" class="dropdown__option" data-curriculum-create><span>Create List +</span></button>';
        curriculumMenu.appendChild(createLi);

        const state = loadByoState();
        Object.values(state.lists).forEach((list) => {
          const li = document.createElement('li');
          const hasItem = !!list.items[productData.variantId];
          li.innerHTML = '<button type="button" class="dropdown__option' + (hasItem ? ' dropdown__option--active' : '') + '" data-curriculum-list="' + list.id + '"><span>' + list.name + '</span>' + (hasItem ? '<span class="dropdown__option-tag">(Already Added)</span>' : '') + '</button>';
          curriculumMenu.appendChild(li);
        });
      };

      const addToList = (listId) => {
        const state = loadByoState();
        const list = state.lists[listId];
        if (!list) return;
        if (list.items[productData.variantId]) return;
        list.items[productData.variantId] = { ...productData };
        state.activeListId = listId;
        saveByoState(state);
        document.dispatchEvent(new CustomEvent('byo:state-changed'));
        if (curriculumValueEl) {
          curriculumValueEl.textContent = 'Added to ' + list.name;
          setTimeout(() => { curriculumValueEl.textContent = 'Add to List'; }, 2000);
        }
      };

      const openCurriculumModal = () => {
        if (!curriculumModal) return;
        curriculumModal.hidden = false;
        if (curriculumInput) {
          curriculumInput.value = '';
          curriculumInput.focus();
        }
      };

      const closeCurriculumModal = () => {
        if (curriculumModal) curriculumModal.hidden = true;
      };

      const createListAndAdd = () => {
        if (!curriculumInput) return;
        const name = curriculumInput.value.trim();
        if (!name) return;

        const state = loadByoState();
        const id = generateListId();
        state.lists[id] = { id, name, items: {} };
        state.activeListId = id;
        saveByoState(state);
        closeCurriculumModal();
        addToList(id);
        renderCurriculumMenu();
      };

      curriculumSection.addEventListener('click', (event) => {
        if (event.target.closest('[data-curriculum-create]')) {
          event.preventDefault();
          const dropdown = curriculumSection.querySelector('[data-dropdown]');
          if (dropdown) dropdown.classList.remove('dropdown--open');
          openCurriculumModal();
          return;
        }

        const listBtn = event.target.closest('[data-curriculum-list]');
        if (listBtn) {
          event.preventDefault();
          addToList(listBtn.dataset.curriculumList);
          renderCurriculumMenu();
          const dropdown = curriculumSection.querySelector('[data-dropdown]');
          if (dropdown) dropdown.classList.remove('dropdown--open');
          return;
        }

        if (event.target.closest('[data-curriculum-modal-create]')) {
          createListAndAdd();
          return;
        }

        if (event.target.closest('[data-curriculum-modal-close]')) {
          closeCurriculumModal();
          return;
        }
      });

      if (curriculumInput) {
        curriculumInput.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            createListAndAdd();
          }
        });
      }

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && curriculumModal && !curriculumModal.hidden) {
          closeCurriculumModal();
        }
      });

      document.addEventListener('byo:state-changed', renderCurriculumMenu);
      renderCurriculumMenu();
    }

    /* Share — Copy Link */
    const copyBtn = document.querySelector('[data-share-copy]');
    if (copyBtn) {
      copyBtn.addEventListener('click', () => {
        navigator.clipboard.writeText(window.location.href).then(() => {
          const svg = copyBtn.innerHTML;
          copyBtn.innerHTML = '<span style="font-size:12px;font-family:var(--font-akzidenz)">Copied!</span>';
          setTimeout(() => { copyBtn.innerHTML = svg; }, 2000);
        });
      });
    }
  };

  const initFaqAccordion = () => {
    document.querySelectorAll('.faq-page__item').forEach(details => {
      const summary = details.querySelector('summary');
      const answer = details.querySelector('.faq-page__answer');
      if (!summary || !answer) return;

      answer.style.height = '0px';

      summary.addEventListener('click', (e) => {
        e.preventDefault();

        if (details.classList.contains('is-open')) {
          details.classList.remove('is-open');
          const anim = answer.animate(
            [{ height: answer.scrollHeight + 'px' }, { height: '0px' }],
            { duration: 320, easing: 'ease' }
          );
          anim.onfinish = () => {
            answer.style.height = '0px';
            details.removeAttribute('open');
          };
        } else {
          details.setAttribute('open', '');
          const endHeight = answer.scrollHeight + 'px';
          const anim = answer.animate(
            [{ height: '0px' }, { height: endHeight }],
            { duration: 320, easing: 'ease' }
          );
          anim.onfinish = () => {
            answer.style.height = 'auto';
            details.classList.add('is-open');
          };
        }
      });
    });
  };

  /* ── Init ────────────────────────────────────────────────────────── */

  initDropdowns();
  initCurriculaFilters();
  initProductPage();
  initFaqAccordion();
  initRotatingBanners();
  initRotatingTracks();
  renderByoCartGroups();
  cleanupByoCartItems();

  let resizeTimer;
  window.addEventListener('resize', () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      initRotatingBanners();
      initRotatingTracks();
    }, 150);
  });

  document.addEventListener('shopify:section:load', (event) => {
    initRotatingBanners(event.target);
    initRotatingTracks(event.target);
  });
});
